import React, { useState, useEffect, useContext, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../../context/AuthContext";
import { fetchWithAuth } from "../../../utils/api";
import "../../../App.css";
import { swalError, swalSuccess, swalWarning } from "../../../utils/swalAlert";
import Modal from "../../ui/Modal";
import ModalTableInput from "../../ui/ModalTableInput";
import Loader from "../../ui/Loader";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { userCountry } from "../../../utils/getUserCountry";
import { defaultCOPOConfig, validateCOWeightage, validateDirectIndirectCOWeightage, validateDirectIndirectPOWeightage } from "../../../utils/copoConfig";
import Select from "react-select";




const UsageCard = ({
  title,
  usageData,
  onRefresh,
  instituteData,
  displayDateOnly,
  link,
  blockSizeData,
}) => {
  const calculateUsagePercentage = () => {
    if (!usageData || !usageData.purchasedSize) return 0;
    return (usageData.usageSize / usageData.purchasedSize) * 100;
  };

  const getUsageStatus = () => {
    if (!usageData || !usageData.purchasedSize) return "normal";
    if (usageData.usageSize > usageData.purchasedSize) return "over";
    const percentage = calculateUsagePercentage();
    if (percentage >= 90) return "critical";
    if (percentage >= 75) return "warning";
    return "normal";
  };

  const usageStatus = getUsageStatus();
  const percentage = calculateUsagePercentage();
  const navigate = useNavigate();
  // Check if this is the Grading Usage card and if expiry date exists
  const isGradingCard = title === "Grading Usage";
  const isExpired =
    instituteData?.gradingCreditExpiryDate &&
    new Date(instituteData.gradingCreditExpiryDate) < new Date();

  return (
    <div className={`usage-card${usageStatus === "over" ? " over" : ""}`}>
      <div className="card-header">
        <h2>{title}</h2>
        <button onClick={()=> navigate(link)} className="view-usage-btn">
          View Detailed Usage
        </button>
        <button onClick={onRefresh} className="refresh-btn">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M4 4V9H4.58152M19.9381 11C19.446 7.05369 16.0796 4 12 4C8.64262 4 5.76829 6.06817 4.58152 9M4.58152 9H9M20 20V15H19.4185M19.4185 15C18.2317 17.9318 15.3574 20 12 20C7.92038 20 4.55399 16.9463 4.06189 13M19.4185 15H15"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Refresh
        </button>
        
      </div>

      <div className="usage-stats">
        <div className="stat-item">
          <div
            className={`stat-number used${usageStatus === "over" ? " over" : ""
              }`}
          >
            {usageData?.usageSize || 0}
          </div>
          <div className="stat-label">Used</div>
        </div>
        <div className="stat-divider">/</div>
        <div className="stat-item">
          <div className="stat-number total">
            {usageData?.purchasedSize || 0}
          </div>
          <div className="stat-label">Total Available</div>
        </div>
      </div>

      <div className="progress-container">
        <div className="progress-header">
          <span>Usage Progress</span>
          <span className={`percentage ${usageStatus}`}>
            {percentage.toFixed(1)}%
          </span>
        </div>
        <div className="progress-bar">
          <div
            className={`progress-fill ${usageStatus}`}
            style={{ width: `${Math.min(percentage, 100)}%` }}
          ></div>
        </div>
      </div>

      <div className="usage-details">
        <div className="detail-row">
          <span>Remaining Credits:</span>
          <span
            className={`remaining-credits${usageStatus === "over" ? " over" : ""
              }`}
          >
            {Math.max(
              0,
              (usageData?.purchasedSize || 0) - (usageData?.usageSize || 0)
            )}
          </span>
        </div>
        <div className="detail-row">
          <span>Status:</span>
          <span className={`status-badge ${usageStatus}`}>
            {usageStatus === "over" && "Over Limit!"}
            {usageStatus === "critical" && "Critical - Limit Reached"}
            {usageStatus === "warning" && "Warning - Approaching Limit"}
            {usageStatus === "normal" && "Normal Usage"}
          </span>
        </div>
      </div>

      {/* Grading Credit Expiry Date - Only show in Grading Usage card */}
      {isGradingCard && (
        <div className="expiry-info">
          <div className="expiry-label">
            <i className="fa-solid fa-calendar-days"></i>
            Credit Expiry Date
          </div>
          <div className={`expiry-date${isExpired ? " expired" : ""}`}>
            {instituteData?.gradingCreditExpiryDate
              ? displayDateOnly(instituteData.gradingCreditExpiryDate)
              : "Not Set"}
          </div>
        </div>
      )}

      {/* Last Purchase Info */}
      {(blockSizeData?.lastPurchasedCredits != null ||
        blockSizeData?.lastPurchasedDate) && (
        <div className="expiry-info" style={{ marginTop: "8px" }}>
          {blockSizeData?.lastPurchasedCredits != null && (
            <div className="detail-row">
              <span>
                <i className="fa-solid fa-coins" style={{ marginRight: "6px" }}></i>
                Last Purchased Credits:
              </span>
              <span style={{ fontWeight: 600 }}>
                {blockSizeData.lastPurchasedCredits}
              </span>
            </div>
          )}
          {blockSizeData?.lastPurchasedDate && (
            <div className="detail-row">
              <span>
                <i className="fa-solid fa-calendar-check" style={{ marginRight: "6px" }}></i>
                Last Purchase Date:
              </span>
              <span style={{ fontWeight: 600 }}>
                {displayDateOnly(blockSizeData.lastPurchasedDate)}
              </span>
            </div>
          )}
        </div>
      )}

      {usageStatus === "over" && (
        <div className="alert-banner over">
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          You have exceeded your usage limit! Please upgrade your plan.
        </div>
      )}
      {usageStatus === "critical" && (
        <div className="alert-banner">
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          You're approaching your usage limit. Consider upgrading your plan.
        </div>
      )}
    </div>
  );
};
export default function ManageInstitute() {
  const { user } = useContext(AuthContext);
  const [instituteData, setInstituteData] = useState(null);
  const [usageData, setUsageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("detail");
  const [editFormData, setEditFormData] = useState({
    name: "",
    tagline: "",
    address: "",
    domain: "",
    phoneNumber: "",
    formattedPhoneNumber: "",
    country: "",
    websiteURL: "",
    isImageCreationAllowed: false,
    gradingCreditExpiryDate: "",
    code: "",
    logo: null,
    logoURL: "",
    annotationPrintingOffsetConfig: [],
    isCOPOEnabled: false,
    // CO-PO Config fields
    coAttainmentMethod: defaultCOPOConfig.coAttainmentMethod,
    // SCORE_BASED_ATTAINMENT criteria
    level1MinScorePercentage: defaultCOPOConfig.scoreBasedAttainment.level1MinScorePercentage,
    level2MinScorePercentage: defaultCOPOConfig.scoreBasedAttainment.level2MinScorePercentage,
    level3MinScorePercentage: defaultCOPOConfig.scoreBasedAttainment.level3MinScorePercentage,
    scoreAveragingBasis: defaultCOPOConfig.scoreAveragingBasis,
    // THRESHOLD_BASED_ATTAINMENT criteria
    minScorePercentage: defaultCOPOConfig.thresholdBasedAttainment.minScorePercentage,
    level1MinStudentPercentage: defaultCOPOConfig.thresholdBasedAttainment.level1MinStudentPercentage,
    level2MinStudentPercentage: defaultCOPOConfig.thresholdBasedAttainment.level2MinStudentPercentage,
    level3MinStudentPercentage: defaultCOPOConfig.thresholdBasedAttainment.level3MinStudentPercentage,
    // Weightages
    internalCOWeightage: defaultCOPOConfig.internalCOWeightage,
    externalCOWeightage: defaultCOPOConfig.externalCOWeightage,
    directCOWeightage: defaultCOPOConfig.directCOWeightage,
    indirectCOWeightage: defaultCOPOConfig.indirectCOWeightage,
    directPOWeightage: defaultCOPOConfig.directPOWeightage,
    indirectPOWeightage: defaultCOPOConfig.indirectPOWeightage,
  });

  const isAnnotationInstitute =
    instituteData?.feedbackMode === "ANNOTATION_ONLY" ||
    instituteData?.feedbackMode === "ANNOTATION_AND_APP_FEEDBACK";

  const checkOffsetConfigChanged = (currentConfig, initialConfig) => {
    const curOffsetConfig = (currentConfig || []).map((c) => ({
      ...(c._id ? { _id: String(c._id) } : {}),
      name: String(c.name ?? "").trim(),
      topOffset: String(c.topOffset ?? "").trim(),
      rightOffset: String(c.rightOffset ?? "").trim(),
    }));
    const initOffsetConfig = (initialConfig || []).map((c) => ({
      ...(c._id ? { _id: String(c._id) } : {}),
      name: String(c.name ?? "").trim(),
      topOffset: String(c.topOffset ?? "").trim(),
      rightOffset: String(c.rightOffset ?? "").trim(),
    }));
    return JSON.stringify(curOffsetConfig) !== JSON.stringify(initOffsetConfig);
  };

  const checkIfChanged = (updatedData, currentInstitute = instituteData) => {
    if (!currentInstitute) return false;

    const basicChanged =
      currentInstitute.name !== updatedData.name ||
      (currentInstitute.tagline || "") !== (updatedData.tagline || "") ||
      (currentInstitute.address || "") !== (updatedData.address || "") ||
      currentInstitute.domain !== updatedData.domain ||
      currentInstitute.phoneNumber !==
        (updatedData.formattedPhoneNumber || updatedData.phoneNumber) ||
      currentInstitute.websiteURL !== updatedData.websiteURL ||
      updatedData.logoChanged === true ||
      updatedData.logo === "";

    if (basicChanged) return true;

    if ((!!currentInstitute.COPOConfig) !== (!!updatedData.isCOPOEnabled)) return true;
    if (updatedData.isCOPOEnabled) {
      if ((currentInstitute.COPOConfig?.coAttainmentMethod ?? defaultCOPOConfig.coAttainmentMethod) !== updatedData.coAttainmentMethod) return true;
      if ((currentInstitute.COPOConfig?.scoreBasedAttainmentCriteria?.level1MinScorePercentage ?? defaultCOPOConfig.scoreBasedAttainment.level1MinScorePercentage) !== (parseFloat(updatedData.level1MinScorePercentage) || 0)) return true;
      if ((currentInstitute.COPOConfig?.scoreBasedAttainmentCriteria?.level2MinScorePercentage ?? defaultCOPOConfig.scoreBasedAttainment.level2MinScorePercentage) !== (parseFloat(updatedData.level2MinScorePercentage) || 0)) return true;
      if ((currentInstitute.COPOConfig?.scoreBasedAttainmentCriteria?.level3MinScorePercentage ?? defaultCOPOConfig.scoreBasedAttainment.level3MinScorePercentage) !== (parseFloat(updatedData.level3MinScorePercentage) || 0)) return true;
      if ((currentInstitute.COPOConfig?.scoreAveragingBasis ?? defaultCOPOConfig.scoreAveragingBasis) !== updatedData.scoreAveragingBasis) return true;
      if ((currentInstitute.COPOConfig?.thresholdBasedAttainmentCriteria?.minScorePercentage ?? defaultCOPOConfig.thresholdBasedAttainment.minScorePercentage) !== (parseFloat(updatedData.minScorePercentage) || 0)) return true;
      if ((currentInstitute.COPOConfig?.thresholdBasedAttainmentCriteria?.level1MinStudentPercentage ?? defaultCOPOConfig.thresholdBasedAttainment.level1MinStudentPercentage) !== (parseFloat(updatedData.level1MinStudentPercentage) || 0)) return true;
      if ((currentInstitute.COPOConfig?.thresholdBasedAttainmentCriteria?.level2MinStudentPercentage ?? defaultCOPOConfig.thresholdBasedAttainment.level2MinStudentPercentage) !== (parseFloat(updatedData.level2MinStudentPercentage) || 0)) return true;
      if ((currentInstitute.COPOConfig?.thresholdBasedAttainmentCriteria?.level3MinStudentPercentage ?? defaultCOPOConfig.thresholdBasedAttainment.level3MinStudentPercentage) !== (parseFloat(updatedData.level3MinStudentPercentage) || 0)) return true;
      if ((currentInstitute.COPOConfig?.internalCOWeightage ?? defaultCOPOConfig.internalCOWeightage) !== (parseFloat(updatedData.internalCOWeightage) || 0)) return true;
      if ((currentInstitute.COPOConfig?.externalCOWeightage ?? defaultCOPOConfig.externalCOWeightage) !== (parseFloat(updatedData.externalCOWeightage) || 0)) return true;
      if ((currentInstitute.COPOConfig?.directCOWeightage ?? defaultCOPOConfig.directCOWeightage) !== (parseFloat(updatedData.directCOWeightage) || 0)) return true;
      if ((currentInstitute.COPOConfig?.indirectCOWeightage ?? defaultCOPOConfig.indirectCOWeightage) !== (parseFloat(updatedData.indirectCOWeightage) || 0)) return true;
      if ((currentInstitute.COPOConfig?.directPOWeightage ?? defaultCOPOConfig.directPOWeightage) !== (parseFloat(updatedData.directPOWeightage) || 0)) return true;
      if ((currentInstitute.COPOConfig?.indirectPOWeightage ?? defaultCOPOConfig.indirectPOWeightage) !== (parseFloat(updatedData.indirectPOWeightage) || 0)) return true;
    }

    if (checkOffsetConfigChanged(updatedData.annotationPrintingOffsetConfig, currentInstitute.annotationPrintingOffsetConfig)) {
      return true;
    }

    return false;
  };

  const addOffsetConfigEntry = () => {
    const newEntry = {
      name: "",
      topOffset: "",
      rightOffset: "",
    };
    setEditFormData((prev) => {
      const updated = {
        ...prev,
        annotationPrintingOffsetConfig: [
          ...(prev.annotationPrintingOffsetConfig || []),
          newEntry,
        ],
      };
      setIsEditChanged(checkIfChanged(updated));
      return updated;
    });
  };

  const removeOffsetConfigEntry = (index) => {
    setEditFormData((prev) => {
      const updated = {
        ...prev,
        annotationPrintingOffsetConfig: (
          prev.annotationPrintingOffsetConfig || []
        ).filter((_, i) => i !== index),
      };
      setIsEditChanged(checkIfChanged(updated));
      return updated;
    });
  };

  const updateOffsetConfigEntry = (index, field, value) => {
    setEditFormData((prev) => {
      const updatedList = [...(prev.annotationPrintingOffsetConfig || [])];
      if (updatedList[index]) {
        updatedList[index] = {
          ...updatedList[index],
          [field]: value,
        };
      }
      const updated = {
        ...prev,
        annotationPrintingOffsetConfig: updatedList,
      };
      setIsEditChanged(checkIfChanged(updated));
      return updated;
    });
  };
  const [coAttainmentMethodOptions, setCoAttainmentMethodOptions] = useState([]);
  const [scoreAveragingBasisOptions, setScoreAveragingBasisOptions] = useState([]);
  const [isEditChanged, setIsEditChanged] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState("");

  const serverUrl = process.env.REACT_APP_SERVER_URL;

  const fetchInstituteData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetchWithAuth(
        `${serverUrl}/api/institute/get/${user.instituteId}`,
        {
          credentials: "include",
        }
      );

      if (response.ok) {
        const data = await response.json();
        setInstituteData(data);
      } else {
        swalError("Failed to fetch institute data. Please try again later.");
      }
    } catch (err) {
      swalError("Error fetching institute data. Please try again later.");
    } finally {
      setLoading(false);
    }
  }, [serverUrl, user.instituteId]);

  const fetchUsageData = useCallback(async () => {
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/institute/get-usage/${user.instituteId}`,
        {
          credentials: "include",
        }
      );

      if (response.ok) {
        const data = await response.json();
        setUsageData(data);
      } else {
        swalError("Failed to fetch usage data. Please try again later.");
      }
    } catch (err) {
      swalError("Error fetching usage data. Please try again later.");
    }
  }, [serverUrl, user.instituteId]);

  const handleEditInstitute = async (e) => {
    e.preventDefault();
    setFormError("");

    if (!isEditChanged) {
      swalWarning("No changes detected");
      return;
    }
    setIsSubmitting(true);

    try {
      // Validate CO Weightages if CO-PO is enabled
      if (editFormData.isCOPOEnabled) {
        const coWeightageValidation = validateCOWeightage(
          editFormData.internalCOWeightage,
          editFormData.externalCOWeightage
        );
        if (!coWeightageValidation.isValid) {
          setFormError(coWeightageValidation.message);
          swalError(coWeightageValidation.message);
          return;
        }
        const directIndirectValidation = validateDirectIndirectCOWeightage(
          editFormData.directCOWeightage,
          editFormData.indirectCOWeightage
        );
        if (!directIndirectValidation.isValid) {
          setFormError(directIndirectValidation.message);
          swalError(directIndirectValidation.message);
          return;
        }
        const poWeightageValidation = validateDirectIndirectPOWeightage(
          editFormData.directPOWeightage,
          editFormData.indirectPOWeightage
        );
        if (!poWeightageValidation.isValid) {
          setFormError(poWeightageValidation.message);
          swalError(poWeightageValidation.message);
          return;
        }
      }

      // Validate annotation offset configs if annotation is enabled
      if (isAnnotationInstitute && Array.isArray(editFormData.annotationPrintingOffsetConfig)) {
        for (let i = 0; i < editFormData.annotationPrintingOffsetConfig.length; i++) {
          const item = editFormData.annotationPrintingOffsetConfig[i];
          if (item.name?.trim() || item.topOffset !== "" || item.rightOffset !== "") {
            if (!item.name?.trim()) {
              const errMsg = `Please enter a Config Name for row ${i + 1} in Annotation Config.`;
              setFormError(errMsg);
              swalError(errMsg);
              setActiveTab("annotationConfig");
              setIsSubmitting(false);
              return;
            }
          }
        }
      }

      const form = new FormData();
      form.append("name", editFormData.name);
      form.append("tagline", editFormData.tagline || "");
      form.append("address", editFormData.address || "");
      form.append("domain", editFormData.domain);
      form.append(
        "phoneNumber",
        editFormData.formattedPhoneNumber || editFormData.phoneNumber
      );
      form.append("websiteURL", editFormData.websiteURL);
      form.append(
        "COPOConfig",
        editFormData.isCOPOEnabled
          ? JSON.stringify({
            coAttainmentMethod: editFormData.coAttainmentMethod,
            scoreAveragingBasis: editFormData.scoreAveragingBasis,
            scoreBasedAttainmentCriteria: editFormData.coAttainmentMethod === 'SCORE_BASED_ATTAINMENT' ? {
              level1MinScorePercentage: parseFloat(editFormData.level1MinScorePercentage) || 0,
              level2MinScorePercentage: parseFloat(editFormData.level2MinScorePercentage) || 0,
              level3MinScorePercentage: parseFloat(editFormData.level3MinScorePercentage) || 0,
            } : undefined,
            thresholdBasedAttainmentCriteria: editFormData.coAttainmentMethod === 'THRESHOLD_BASED_ATTAINMENT' ? {
              minScorePercentage: parseFloat(editFormData.minScorePercentage) || 0,
              level1MinStudentPercentage: parseFloat(editFormData.level1MinStudentPercentage) || 0,
              level2MinStudentPercentage: parseFloat(editFormData.level2MinStudentPercentage) || 0,
              level3MinStudentPercentage: parseFloat(editFormData.level3MinStudentPercentage) || 0,
            } : undefined,
            internalCOWeightage: parseFloat(editFormData.internalCOWeightage) || 0,
            externalCOWeightage: parseFloat(editFormData.externalCOWeightage) || 0,
            directCOWeightage: parseFloat(editFormData.directCOWeightage) || 0,
            indirectCOWeightage: parseFloat(editFormData.indirectCOWeightage) || 0,
            directPOWeightage: parseFloat(editFormData.directPOWeightage) || 0,
            indirectPOWeightage: parseFloat(editFormData.indirectPOWeightage) || 0,
          })
          : null
      );

      if (isAnnotationInstitute) {
        const offsetConfigToSend = Array.isArray(
          editFormData.annotationPrintingOffsetConfig
        )
          ? editFormData.annotationPrintingOffsetConfig
              .filter(
                (item) =>
                  item.name ||
                  item.topOffset !== "" ||
                  item.rightOffset !== ""
              )
              .map((item) => ({
                ...(item._id ? { _id: item._id } : {}),
                name: item.name || "",
                topOffset:
                  item.topOffset !== "" ? parseFloat(item.topOffset) || 0 : 0,
                rightOffset:
                  item.rightOffset !== ""
                    ? parseFloat(item.rightOffset) || 0
                    : 0,
              }))
          : [];
        form.append(
          "annotationPrintingOffsetConfig",
          JSON.stringify(offsetConfigToSend)
        );
      }

      if (editFormData.logo === "") {
        form.append("logo", "");
      } else if (editFormData.logo) {
        form.append("logo", editFormData.logo);
      }

      const response = await fetchWithAuth(
        `${serverUrl}/api/institute/update/${user.instituteId}`,
        {
          method: "PUT",
          credentials: "include",
          body: form,
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to update institute");
      }

      const updatedInstitute = await response.json();
      setInstituteData(updatedInstitute);
      setIsEditModalOpen(false);
      setIsEditChanged(false);
      swalSuccess("Institute updated successfully");
    } catch (err) {
      setFormError(err.message);
      swalError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const userDate = useCallback((dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    // Return only the date part in YYYY-MM-DD format for date input
    return date.toISOString().slice(0, 10);
  }, []);

  // Add a new function for display purposes
  const displayDateOnly = useCallback((dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString(navigator.language, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }, []);

  const handleEditInputChange = (e) => {
    const { name, type, value, checked, files } = e.target;
    let logoChanged = false;

    setEditFormData((prev) => {
      let updatedData = { ...prev };
      if (name === "logo") {
        const file = files[0];

        // Validate file size (1MB = 1024 * 1024 bytes)
        if (file && file.size > 1024 * 1024) {
          swalError("Logo file size must be less than 1MB. Please choose a smaller file.");
          e.target.value = ""; // Clear the input
          return prev; // Don't update state
        }

        updatedData.logo = file || null;
        const currentLogoFilename = prev.logoURL
          ? prev.logoURL.split("/").pop()
          : "";
        logoChanged = file && file.name !== currentLogoFilename;
        updatedData.logoChanged = logoChanged;
      } else {
        updatedData[name] = type === "checkbox" ? checked : value;
      }

      setIsEditChanged(checkIfChanged(updatedData));
      return updatedData;
    });
  };

  const handleEditSelectChange = (selectedOption, actionMeta) => {
    const { name } = actionMeta;
    setEditFormData((prev) => {
      const updatedData = {
        ...prev,
        [name]: selectedOption ? selectedOption.value : "",
      };

      setIsEditChanged(checkIfChanged(updatedData));
      return updatedData;
    });
  };

  const handleRemoveLogo = () => {
    setEditFormData((prev) => {
      const updatedData = {
        ...prev,
        logo: "",
        logoURL: "",
        logoChanged: true,
      };
      setIsEditChanged(true);
      return updatedData;
    });
  };

  const openEditModal = () => {
    setActiveTab("detail");
    setEditFormData({
      name: instituteData.name || "",
      tagline: instituteData.tagline || "",
      address: instituteData.address || "",
      domain: instituteData.domain || "",
      phoneNumber: instituteData.phoneNumber || "",
      formattedPhoneNumber: instituteData.phoneNumber || "",
      country: "",
      websiteURL: instituteData.websiteURL || "",
      isImageCreationAllowed: instituteData.isImageCreationAllowed || false,
      gradingCreditExpiryDate: instituteData.gradingCreditExpiryDate
        ? userDate(instituteData.gradingCreditExpiryDate)
        : "",
      code: instituteData.code || "",
      logo: null,
      logoURL: instituteData.logoURL || "",
      annotationPrintingOffsetConfig: Array.isArray(
        instituteData.annotationPrintingOffsetConfig
      )
        ? instituteData.annotationPrintingOffsetConfig.map((item) => ({
            ...(item._id ? { _id: item._id } : {}),
            name: item.name || "",
            topOffset: item.topOffset ?? 0,
            rightOffset: item.rightOffset ?? 0,
          }))
        : [],
      isCOPOEnabled: !!instituteData.COPOConfig,
      coAttainmentMethod: instituteData.COPOConfig?.coAttainmentMethod ?? defaultCOPOConfig.coAttainmentMethod,
      // SCORE_BASED_ATTAINMENT criteria
      level1MinScorePercentage: instituteData.COPOConfig?.scoreBasedAttainmentCriteria?.level1MinScorePercentage ?? defaultCOPOConfig.scoreBasedAttainment.level1MinScorePercentage,
      level2MinScorePercentage: instituteData.COPOConfig?.scoreBasedAttainmentCriteria?.level2MinScorePercentage ?? defaultCOPOConfig.scoreBasedAttainment.level2MinScorePercentage,
      level3MinScorePercentage: instituteData.COPOConfig?.scoreBasedAttainmentCriteria?.level3MinScorePercentage ?? defaultCOPOConfig.scoreBasedAttainment.level3MinScorePercentage,
      scoreAveragingBasis: instituteData.COPOConfig?.scoreAveragingBasis ?? defaultCOPOConfig.scoreAveragingBasis,
      // THRESHOLD_BASED_ATTAINMENT criteria
      minScorePercentage: instituteData.COPOConfig?.thresholdBasedAttainmentCriteria?.minScorePercentage ?? defaultCOPOConfig.thresholdBasedAttainment.minScorePercentage,
      level1MinStudentPercentage: instituteData.COPOConfig?.thresholdBasedAttainmentCriteria?.level1MinStudentPercentage ?? defaultCOPOConfig.thresholdBasedAttainment.level1MinStudentPercentage,
      level2MinStudentPercentage: instituteData.COPOConfig?.thresholdBasedAttainmentCriteria?.level2MinStudentPercentage ?? defaultCOPOConfig.thresholdBasedAttainment.level2MinStudentPercentage,
      level3MinStudentPercentage: instituteData.COPOConfig?.thresholdBasedAttainmentCriteria?.level3MinStudentPercentage ?? defaultCOPOConfig.thresholdBasedAttainment.level3MinStudentPercentage,
      // Weightages
      internalCOWeightage: instituteData.COPOConfig?.internalCOWeightage ?? defaultCOPOConfig.internalCOWeightage,
      externalCOWeightage: instituteData.COPOConfig?.externalCOWeightage ?? defaultCOPOConfig.externalCOWeightage,
      directCOWeightage: instituteData.COPOConfig?.directCOWeightage ?? defaultCOPOConfig.directCOWeightage,
      indirectCOWeightage: instituteData.COPOConfig?.indirectCOWeightage ?? defaultCOPOConfig.indirectCOWeightage,
      directPOWeightage: instituteData.COPOConfig?.directPOWeightage ?? defaultCOPOConfig.directPOWeightage,
      indirectPOWeightage: instituteData.COPOConfig?.indirectPOWeightage ?? defaultCOPOConfig.indirectPOWeightage,
    });
    setIsEditModalOpen(true);
    setIsEditChanged(false);
  };

  const fetchLOVs = useCallback(async () => {
    try {
      const coAttainmentMethodResponse = await fetchWithAuth(
        `${serverUrl}/api/lov/get-all-by-type/coAttainmentMethod`
      );
      if (!coAttainmentMethodResponse) return;
      if (!coAttainmentMethodResponse.ok) throw new Error("Failed to fetch LOVs");
      const coAttainmentMethodLOVs = await coAttainmentMethodResponse.json();

      const coAttainmentMethods = coAttainmentMethodLOVs.map((method) => ({
        value: method.value,
        label: method.value,
      }));

      setCoAttainmentMethodOptions(coAttainmentMethods);

      const scoreAveragingBasisResponse = await fetchWithAuth(
        `${serverUrl}/api/lov/get-all-by-type/scoreAveragingBasis`
      );
      if (scoreAveragingBasisResponse && scoreAveragingBasisResponse.ok) {
        const scoreAveragingBasisLOVs = await scoreAveragingBasisResponse.json();
        const scoreAveragingBasisOpts = scoreAveragingBasisLOVs.map((basis) => ({
          value: basis.value,
          label: basis.value,
        }));
        setScoreAveragingBasisOptions(scoreAveragingBasisOpts);
      }

    } catch (err) {
      console.error("Failed to fetch LOVs:", err);
    }
  }, [serverUrl]);

  useEffect(() => {
    fetchInstituteData();
    fetchUsageData();
    fetchLOVs();
  }, [fetchInstituteData, fetchUsageData, fetchLOVs]);

  if (loading) {
    return (
      <div className="manage-institute">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading institute data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="manage-institute">
      {/* Institute Details Card */}
      <div className="institute-details-card">
        <div className="institute-header">
          <div className="institute-logo">
            <img
              src={instituteData?.logoURL || "/imgs/logo.svg"}
              alt="Institute Logo"
              onError={(e) => {
                e.target.src = "/imgs/logo.svg";
              }}
            />
          </div>
        <div className="institute-info">
          <h2>{instituteData?.name}</h2>
          {instituteData?.tagline && (
            <p style={{ margin: "2px 0 4px", fontSize: "0.95rem", color: "#64748b", fontStyle: "italic" }}>
              {instituteData.tagline}
            </p>
          )}
          <div className="institute-code">Code: {instituteData?.code}</div>
          {instituteData?.address && (
            <div style={{ fontSize: "0.85rem", color: "#475569", marginTop: "4px", display: "flex", alignItems: "center", gap: "5px" }}>
              <i className="fa-solid fa-location-dot" style={{ color: "var(--color-1, #f09a21)" }}></i>
              <span>{instituteData.address}</span>
            </div>
          )}
        </div>
          <div className="institute-actions">
            <button onClick={openEditModal} className="edit-institute-btn" type="button">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M18.5 2.49998C18.8978 2.10216 19.4374 1.87866 20 1.87866C20.5626 1.87866 21.1022 2.10216 21.5 2.49998C21.8978 2.89781 22.1213 3.43737 22.1213 3.99998C22.1213 4.56259 21.8978 5.10216 21.5 5.49998L12 15L8 16L9 12L18.5 2.49998Z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Edit Details
            </button>
          </div>
        </div>
      </div>

      {/* Usage Section */}
      <div className="usage-section">
        <h2>Usage Overview</h2>
        <div className="usage-cards-container">
          {usageData?.combinedGrading && (
            <UsageCard
              title="Grading Usage"
              usageData={usageData.combinedGrading}
              onRefresh={fetchUsageData}
              instituteData={instituteData}
              displayDateOnly={displayDateOnly}
              link="/admin/manage-institute/grading-credit-usage/?creditType=Grader"
              blockSizeData={instituteData?.combinedGradingBlockSize}
            />
          )}
          {usageData?.questionBlock && (
            <UsageCard
              title="Question Bank Usage"
              usageData={usageData.questionBlock}
              onRefresh={fetchUsageData}
              instituteData={instituteData}
              displayDateOnly={displayDateOnly}
              link="/admin/manage-institute/grading-credit-usage/?creditType=QBGenerator"
              blockSizeData={instituteData?.questionBlockSize}
            />
          )}
        </div>
        {usageData?.whatsAppNotification && (
          <div className="usage-cards-container" style={{ marginTop: "20px" }}>
            <UsageCard
              title="WhatsApp Notification Usage"
              usageData={usageData.whatsAppNotification}
              onRefresh={fetchUsageData}
              instituteData={instituteData}
              displayDateOnly={displayDateOnly}
              link="/admin/whatsapp-notifications"
            />
          </div>
        )}
      </div>

      {/* Edit Institute Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => !isSubmitting && setIsEditModalOpen(false)}
        maxWidth={isAnnotationInstitute ? "750px" : "550px"}
      >
        <h2>Edit Institute Details</h2>

        <div className="manage-institute-modal-tabs">
          <button
            type="button"
            className={`manage-institute-tab-btn ${activeTab === "detail" ? "active" : ""}`}
            onClick={() => setActiveTab("detail")}
          >
            General
          </button>
          {isAnnotationInstitute && (
            <button
              type="button"
              className={`manage-institute-tab-btn ${activeTab === "annotationConfig" ? "active" : ""}`}
              onClick={() => setActiveTab("annotationConfig")}
            >
              Annotation Config
            </button>
          )}
        </div>

        <form onSubmit={handleEditInstitute} encType="multipart/form-data">
          {(!isAnnotationInstitute || activeTab === "detail") && (
            <div className="manage-institute-tab-pane">
              <div className="form-group">
                <label htmlFor="edit-name">
                  <i className="fa-solid fa-asterisk requiredIcon"></i> Institute
                  Name
                </label>
                <input
                  type="text"
                  id="edit-name"
                  name="name"
                  value={editFormData.name}
                  onChange={handleEditInputChange}
                  required
                  placeholder="Enter institute name"
                />
              </div>
              <div className="form-group">
                <label htmlFor="edit-tagline">Tagline</label>
                <input
                  type="text"
                  id="edit-tagline"
                  name="tagline"
                  value={editFormData.tagline}
                  onChange={handleEditInputChange}
                  placeholder="Enter institute tagline"
                />
              </div>
              <div className="form-group">
                <label htmlFor="edit-address">Address</label>
                <input
                  type="text"
                  id="edit-address"
                  name="address"
                  value={editFormData.address}
                  onChange={handleEditInputChange}
                  placeholder="Enter institute address"
                />
              </div>
              <div className="form-group">
                <label htmlFor="edit-logo">Institute Logo (jpg/png, max 1MB)</label>
                <input
                  type="file"
                  id="edit-logo"
                  name="logo"
                  accept=".jpg,.jpeg,.png"
                  onChange={handleEditInputChange}
                />
                {editFormData.logoURL && (
                  <div
                    style={{ marginTop: "8px", fontSize: "0.95em", color: "#555" }}
                  >
                    Current logo: <b>{editFormData.logoURL.split("/").pop()}</b>
                  </div>
                )}
                {editFormData.logoURL && (
                  <div style={{ marginTop: "8px" }}>
                    <img
                      src={editFormData.logoURL}
                      alt="Current Logo"
                      style={{ maxWidth: "100px", maxHeight: "100px" }}
                    />
                  </div>
                )}
                {editFormData.logoURL && (
                  <button
                    type="button"
                    className="remove-logo-btn"
                    onClick={handleRemoveLogo}
                    disabled={isSubmitting}
                  >
                    <i className="fa-solid fa-xmark"></i> Remove Logo
                  </button>
                )}
              </div>
              <div className="form-group">
                <label htmlFor="edit-phoneNumber">Phone Number</label>
                <PhoneInput
                  placeholder="Phone Number"
                  country={userCountry}
                  value={editFormData.phoneNumber}
                  onChange={(value, data, event, formattedValue) => {
                    setEditFormData((prev) => {
                      const updatedData = {
                        ...prev,
                        phoneNumber: value,
                        formattedPhoneNumber: formattedValue,
                        country: data.name,
                      };
                      setIsEditChanged(checkIfChanged(updatedData));
                      return updatedData;
                    });
                  }}
                  inputStyle={{
                    height: "40px",
                    fontSize: "14px",
                    width: "100%",
                    paddingLeft: "48px",
                  }}
                  buttonStyle={{
                    height: "40px",
                  }}
                  containerStyle={{
                    width: "100%",
                  }}
                  enableSearch={true}
                  disableSearchIcon={true}
                  inputProps={{
                    name: "phoneNumber",
                  }}
                  dropdownClass="phone-input-dropdown"
                  dropdownStyle={{
                    maxHeight: "200px",
                    overflowX: "hidden",
                  }}
                />
              </div>

              <div className="form-group">
                <label htmlFor="edit-websiteURL">Website URL</label>
                <input
                  type="url"
                  id="edit-websiteURL"
                  name="websiteURL"
                  value={editFormData.websiteURL}
                  onChange={handleEditInputChange}
                  placeholder="Enter website URL"
                />
              </div>

              {editFormData.isCOPOEnabled && (
                <div className="copo-config-section" style={{ marginTop: "20px", borderTop: "1px solid #eee", paddingTop: "10px" }}>
                  <h3>CO/PO Configuration</h3>

                  <div className="form-group">
                    <label htmlFor="edit-coAttainmentMethod">CO Attainment Method</label>
                    <Select
                      name="coAttainmentMethod"
                      options={coAttainmentMethodOptions}
                      value={coAttainmentMethodOptions.find(
                        (option) => option.value === editFormData.coAttainmentMethod
                      )}
                      onChange={handleEditSelectChange}
                      placeholder="Select attainment method..."
                      required={editFormData.isCOPOEnabled}
                    />
                  </div>

                  {editFormData.coAttainmentMethod === 'THRESHOLD_BASED_ATTAINMENT' && (
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", backgroundColor: "#f9f9f9", padding: "10px", borderRadius: "5px", marginBottom: "10px" }}>
                      <div className="form-group">
                        <label htmlFor="edit-minScorePercentage">Min Score %</label>
                        <input type="number" id="edit-minScorePercentage" name="minScorePercentage" value={editFormData.minScorePercentage} onChange={handleEditInputChange} min="0" max="100" />
                      </div>
                      <div className="form-group">
                        <label htmlFor="edit-level1MinStudentPercentage">Level 1 Min Student %</label>
                        <input type="number" id="edit-level1MinStudentPercentage" name="level1MinStudentPercentage" value={editFormData.level1MinStudentPercentage} onChange={handleEditInputChange} min="0" max="100" />
                      </div>
                      <div className="form-group">
                        <label htmlFor="edit-level2MinStudentPercentage">Level 2 Min Student %</label>
                        <input type="number" id="edit-level2MinStudentPercentage" name="level2MinStudentPercentage" value={editFormData.level2MinStudentPercentage} onChange={handleEditInputChange} min="0" max="100" />
                      </div>
                      <div className="form-group">
                        <label htmlFor="edit-level3MinStudentPercentage">Level 3 Min Student %</label>
                        <input type="number" id="edit-level3MinStudentPercentage" name="level3MinStudentPercentage" value={editFormData.level3MinStudentPercentage} onChange={handleEditInputChange} min="0" max="100" />
                      </div>
                    </div>
                  )}

                  {editFormData.coAttainmentMethod === 'SCORE_BASED_ATTAINMENT' && (
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", backgroundColor: "#f9f9f9", padding: "10px", borderRadius: "5px", marginBottom: "10px" }}>
                      <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                        <label htmlFor="edit-scoreAveragingBasis">Score Averaging Basis</label>
                        <Select
                          name="scoreAveragingBasis"
                          options={scoreAveragingBasisOptions}
                          value={scoreAveragingBasisOptions.find(
                            (option) => option.value === editFormData.scoreAveragingBasis
                          )}
                          onChange={handleEditSelectChange}
                          placeholder="Select averaging basis..."
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="edit-level1MinScorePercentage">Level 1 Min Score %</label>
                        <input type="number" id="edit-level1MinScorePercentage" name="level1MinScorePercentage" value={editFormData.level1MinScorePercentage} onChange={handleEditInputChange} min="0" max="100" />
                      </div>
                      <div className="form-group">
                        <label htmlFor="edit-level2MinScorePercentage">Level 2 Min Score %</label>
                        <input type="number" id="edit-level2MinScorePercentage" name="level2MinScorePercentage" value={editFormData.level2MinScorePercentage} onChange={handleEditInputChange} min="0" max="100" />
                      </div>
                      <div className="form-group">
                        <label htmlFor="edit-level3MinScorePercentage">Level 3 Min Score %</label>
                        <input type="number" id="edit-level3MinScorePercentage" name="level3MinScorePercentage" value={editFormData.level3MinScorePercentage} onChange={handleEditInputChange} min="0" max="100" />
                      </div>
                    </div>
                  )}

                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                    <div className="form-group">
                      <label htmlFor="edit-internalCOWeightage">Internal CO Weightage</label>
                      <input type="number" id="edit-internalCOWeightage" name="internalCOWeightage" value={editFormData.internalCOWeightage} onChange={handleEditInputChange} min="0" max="1" step="0.25" />
                    </div>
                    <div className="form-group">
                      <label htmlFor="edit-externalCOWeightage">External CO Weightage</label>
                      <input type="number" id="edit-externalCOWeightage" name="externalCOWeightage" value={editFormData.externalCOWeightage} onChange={handleEditInputChange} min="0" max="1" step="0.25" />
                    </div>
                  </div>
                  {!validateCOWeightage(editFormData.internalCOWeightage, editFormData.externalCOWeightage).isValid && (
                    <p style={{ color: '#dc3545', fontSize: '0.85rem', margin: '4px 0 10px 0', fontWeight: '500' }}>
                      {validateCOWeightage(editFormData.internalCOWeightage, editFormData.externalCOWeightage).message}
                    </p>
                  )}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                    <div className="form-group">
                      <label htmlFor="edit-directCOWeightage">Direct CO Weightage</label>
                      <input type="number" id="edit-directCOWeightage" name="directCOWeightage" value={editFormData.directCOWeightage} onChange={handleEditInputChange} min="0" max="1" step="0.25" />
                    </div>
                    <div className="form-group">
                      <label htmlFor="edit-indirectCOWeightage">Indirect CO Weightage</label>
                      <input type="number" id="edit-indirectCOWeightage" name="indirectCOWeightage" value={editFormData.indirectCOWeightage} onChange={handleEditInputChange} min="0" max="1" step="0.25" />
                    </div>
                  </div>
                  {!validateDirectIndirectCOWeightage(editFormData.directCOWeightage, editFormData.indirectCOWeightage).isValid && (
                    <p style={{ color: '#dc3545', fontSize: '0.85rem', margin: '4px 0 10px 0', fontWeight: '500' }}>
                      {validateDirectIndirectCOWeightage(editFormData.directCOWeightage, editFormData.indirectCOWeightage).message}
                    </p>
                  )}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                    <div className="form-group">
                      <label htmlFor="edit-directPOWeightage">Direct PO Weightage</label>
                      <input type="number" id="edit-directPOWeightage" name="directPOWeightage" value={editFormData.directPOWeightage} onChange={handleEditInputChange} min="0" max="1" step="0.25" />
                    </div>
                    <div className="form-group">
                      <label htmlFor="edit-indirectPOWeightage">Indirect PO Weightage</label>
                      <input type="number" id="edit-indirectPOWeightage" name="indirectPOWeightage" value={editFormData.indirectPOWeightage} onChange={handleEditInputChange} min="0" max="1" step="0.25" />
                    </div>
                  </div>
                  {!validateDirectIndirectPOWeightage(editFormData.directPOWeightage, editFormData.indirectPOWeightage).isValid && (
                    <p style={{ color: '#dc3545', fontSize: '0.85rem', margin: '4px 0 10px 0', fontWeight: '500' }}>
                      {validateDirectIndirectPOWeightage(editFormData.directPOWeightage, editFormData.indirectPOWeightage).message}
                    </p>
                  )}
                </div>
              )}
              {/* Is ImageCreation Allowd */}
              <div className="form-group">
                <label htmlFor="edit-websiteURL">
                  Image Creation :{" "}
                  {editFormData.isImageCreationAllowed
                    ? "Allowed ✅"
                    : "Not Allowed ❌"}
                </label>{" "}
              </div>
              {/* Credit Expire Date */}
              <div className="form-group">
                <label htmlFor="edit-gradingCreditExpiryDate">
                  Grading Credit Expiry Date :{" "}
                  <span
                    style={{
                      color:
                        instituteData?.gradingCreditExpiryDate &&
                          new Date(instituteData.gradingCreditExpiryDate) < new Date()
                          ? "red"
                          : "inherit",
                    }}
                  >
                    {editFormData.gradingCreditExpiryDate
                      ? displayDateOnly(instituteData.gradingCreditExpiryDate)
                      : "Not Set"}
                  </span>
                </label>
              </div>
            </div>
          )}

          {isAnnotationInstitute && activeTab === "annotationConfig" && (
            <div className="manage-institute-tab-pane">
              <div className="form-group">
                <label>Annotation Printing Offset Configurations</label>
                <ModalTableInput
                  headers={[
                    {
                      key: "name",
                      label: "Config Name",
                      width: "1.5fr",
                      required: true,
                    },
                    {
                      key: "topOffset",
                      label: "Top Offset",
                      width: "1fr",
                      required: true,
                    },
                    {
                      key: "rightOffset",
                      label: "Right Offset",
                      width: "1fr",
                      required: true,
                    },
                    {
                      key: "actions",
                      label: "Actions",
                      width: "0.6fr",
                    },
                  ]}
                  rows={editFormData.annotationPrintingOffsetConfig || []}
                  onRowChange={(index, field, value) =>
                    updateOffsetConfigEntry(index, field, value)
                  }
                  onRowRemove={(index) => removeOffsetConfigEntry(index)}
                  onRowAdd={addOffsetConfigEntry}
                  addButtonText="Add Offset Config"
                  renderCell={(header, row, rowIndex) => {
                    switch (header.key) {
                      case "name":
                        return (
                          <input
                            type="text"
                            value={row.name || ""}
                            onChange={(e) =>
                              updateOffsetConfigEntry(
                                rowIndex,
                                "name",
                                e.target.value
                              )
                            }
                            placeholder="e.g. Unit Test"
                            className="modal-table-input"
                          />
                        );
                      case "topOffset":
                        return (
                          <input
                            type="number"
                            value={row.topOffset ?? ""}
                            onChange={(e) =>
                              updateOffsetConfigEntry(
                                rowIndex,
                                "topOffset",
                                e.target.value
                              )
                            }
                            placeholder="e.g. 0.4 in cm"
                            step="any"
                            className="modal-table-input"
                          />
                        );
                      case "rightOffset":
                        return (
                          <input
                            type="number"
                            value={row.rightOffset ?? ""}
                            onChange={(e) =>
                              updateOffsetConfigEntry(
                                rowIndex,
                                "rightOffset",
                                e.target.value
                              )
                            }
                            placeholder="e.g. 0.2 in cm"
                            step="any"
                            className="modal-table-input"
                          />
                        );
                      case "actions":
                        return (
                          <button
                            type="button"
                            onClick={() => removeOffsetConfigEntry(rowIndex)}
                            className="modal-table-remove-btn"
                            title="Remove this configuration"
                          >
                            <i className="fa-solid fa-trash"></i>
                          </button>
                        );
                      default:
                        return null;
                    }
                  }}
                />
              </div>
            </div>
          )}

          {formError && <div className="error-message">{formError}</div>}
          <button
            type="submit"
            className="btn-submit"
            disabled={!isEditChanged || isSubmitting}
          >
            {isSubmitting ? (
              <Loader
                style={{ fontSize: "1em", color: "white", top: "0", left: "0" }}
              />
            ) : (
              <>
                <i className="fa-solid fa-floppy-disk"></i> Save Changes
              </>
            )}
          </button>
        </form>
      </Modal>
    </div>
  );
}
